/**
 * Card — the single surface primitive.
 *
 * Used by: dashboard KPI tiles, every form section, list containers, the
 * translation side-by-side panes, POS order panels.
 *
 * There is deliberately no `Card.Body` sub-component: nesting padding rules is
 * how design systems turn into puzzles. `padded={false}` plus your own layout
 * covers the cases where the card wraps a full-bleed table.
 */
import type { ReactNode } from 'react';
import { cn } from '../utils.js';

export interface CardProps {
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  /** Buttons / filters aligned to the right of the title row. */
  readonly actions?: ReactNode;
  readonly footer?: ReactNode;
  readonly padded?: boolean;
  readonly className?: string;
  readonly bodyClassName?: string;
  readonly children?: ReactNode;
}

export function Card({
  title,
  description,
  actions,
  footer,
  padded = true,
  className,
  bodyClassName,
  children,
}: CardProps): React.JSX.Element {
  const hasHeader = title !== undefined || description !== undefined || actions !== undefined;

  return (
    <section className={cn('rounded-lg bg-surface shadow-card ring-1 ring-line', className)}>
      {hasHeader ? (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3 sm:px-5">
          <div className="min-w-0">
            {title !== undefined ? <h2 className="truncate text-base font-semibold text-fg">{title}</h2> : null}
            {description !== undefined ? <p className="mt-0.5 text-sm text-fg-muted">{description}</p> : null}
          </div>
          {actions !== undefined ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}

      <div className={cn(padded && 'px-4 py-4 sm:px-5', bodyClassName)}>{children}</div>

      {footer !== undefined ? (
        <footer className="border-t border-line bg-surface-alt/60 px-4 py-3 sm:px-5">{footer}</footer>
      ) : null}
    </section>
  );
}

/**
 * StatCard — one KPI number.
 *
 * Used by: the Admin overview dashboard and the branch comparison strip.
 * `delta` is a pre-formatted string (e.g. "+12,4%") because the comparison
 * basis differs per metric and only the caller knows it.
 */
export interface StatCardProps {
  readonly label: string;
  readonly value: ReactNode;
  readonly sublabel?: ReactNode;
  readonly delta?: { readonly text: string; readonly direction: 'up' | 'down' | 'flat' };
  readonly icon?: ReactNode;
  readonly className?: string;
}

export function StatCard({ label, value, sublabel, delta, icon, className }: StatCardProps): React.JSX.Element {
  return (
    <div className={cn('rounded-lg bg-surface p-4 shadow-card ring-1 ring-line', className)}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-fg-muted">{label}</p>
        {icon !== undefined ? (
          <span aria-hidden="true" className="text-fg-subtle">
            {icon}
          </span>
        ) : null}
      </div>
      <p className="sa-tabular mt-2 text-2xl font-semibold text-fg">{value}</p>
      <div className="mt-1 flex items-center gap-2">
        {delta !== undefined ? (
          <span
            className={cn(
              'sa-tabular text-xs font-medium',
              delta.direction === 'up' && 'text-success-fg',
              delta.direction === 'down' && 'text-danger-fg',
              delta.direction === 'flat' && 'text-fg-subtle',
            )}
          >
            {delta.direction === 'up' ? '▲' : delta.direction === 'down' ? '▼' : '■'} {delta.text}
          </span>
        ) : null}
        {sublabel !== undefined ? <span className="text-xs text-fg-subtle">{sublabel}</span> : null}
      </div>
    </div>
  );
}
